import { INTENT_TYPE } from '../../constants';
import { type IntentMatcher } from '../../types';

/**
 * PORTED FROM: `JqlUtils.isJql`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/util/JqlUtils.java#lines-121
 *
 * A direct port of the backend's regex-based JQL detection. It is deliberately
 * **not** a real JQL parse: it recognises the *shape* of a query whose first
 * token is a known field followed by an operator, and nothing more.
 *
 * That distinction matters: `foo = bar` is syntactically valid JQL but is not
 * detected, because `foo` is not in the field allowlist. Parsing properly would
 * disagree with the backend on exactly those inputs.
 */

/**
 * PORTED FROM: `JqlUtils.JQL_FIELDS`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/util/JqlUtils.java#lines-9
 *
 * Kept in the upstream order so the two lists can be diffed by eye.
 */
const JQL_FIELDS: readonly string[] = [
	'affectedversion',
	'approvals',
	'assignee',
	'attachments',
	'category',
	'change-gating-type',
	'comment',
	'component',
	'created',
	'createddate',
	'creator',
	'description',
	'due',
	'duedate',
	'environment',
	'filter',
	'request',
	'savedfilter',
	'searchrequest',
	'fixversion',
	'hierarchylevel',
	'journeyid',
	'journeyparentoftypeid',
	'journeytypeid',
	'journeytypeversion',
	'journeyworkitemid',
	'labels',
	'lastviewed',
	'level',
	'organizations',
	'originalestimate',
	'timeoriginalestimate',
	'parent',
	'parentproject',
	'priority',
	'project',
	'projecttype',
	'remainingestimate',
	'timeestimate',
	'reporter',
	'request-channel-type',
	'request-last-activity-time',
	'resolution',
	'resolved',
	'resolutiondate',
	'sprint',
	'status',
	'summary',
	'text',
	'textfields',
	'timespent',
	'type',
	'worktype',
	'updated',
	'updateddate',
	'voter',
	'votes',
	'watcher',
	'watchers',
	'worklogcomment',
	'worklogdate',
	'workitemkey',
	'id',
	'workitem',
	'key',
	'workitemlink',
	'workitemlinktype',
	'workratio',
	'space',
];

/**
 * PORTED FROM: `JqlUtils.CHANGED_OPERATOR_FIELDS`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/util/JqlUtils.java#lines-80
 */
const CHANGED_OPERATOR_FIELDS: readonly string[] = [
	'assignee',
	'fixversion',
	'priority',
	'reporter',
	'resolution',
	'status',
];

/** Equivalent of Java's `Pattern.quote` for a single alternation branch. */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Builds the field alternation.
 *
 * Sorted longest-first. Upstream this comes from a `Set.of(...)`, whose
 * iteration order is unspecified, and correctness there relies on regex
 * backtracking to eventually reach the longer alternative (so `createddate = x`
 * is not mis-read as the field `created`). Backtracking would get us the same
 * answer, but ordering explicitly makes the behaviour deterministic and
 * independent of how the list happens to be written.
 */
const toFieldsPattern = (fields: readonly string[]): string =>
	[...fields]
		.sort((a, b) => b.length - a.length)
		.map(escapeRegExp)
		.join('|');

const FIELDS_PATTERN = toFieldsPattern(JQL_FIELDS);
const CHANGED_FIELDS_PATTERN = toFieldsPattern(CHANGED_OPERATOR_FIELDS);

/**
 * Emulates a Java atomic group `(?>...)`, which JavaScript has no syntax for.
 *
 * A lookahead captures the longest alternative that matches at this position,
 * and the backreference then consumes it — so the engine can never backtrack
 * into the group to try a shorter alternative.
 *
 * This is load-bearing, not pedantry. Upstream both operator groups are atomic.
 * Without it, `votes >=` would be treated as JQL: `>=` matches, the trailing
 * `.+` finds nothing left, and a non-atomic group would backtrack to `>` and
 * let `=` satisfy `.+`. Atomic matching refuses that, and so does the backend.
 *
 * The group is *named* rather than numbered so the backreference cannot be
 * knocked out of alignment by any other capture group in the pattern.
 */
const atomic = (alternation: string): string => `(?=(?<op>${alternation}))\\k<op>`;

const SYMBOL_OP_PATTERN = atomic('!=|>=|<=|!~|=|>|<|~');
const WORD_OP_PATTERN = atomic('was not in|not in|is not|was in|was not|in|not|is|was|changed');

/**
 * Java's `Matcher.matches()` requires the *entire* input to match, so every
 * pattern is anchored at both ends. `.` excludes newlines in both languages,
 * so no dot-all flag is used.
 */
const SYMBOL_JQL_PATTERN = new RegExp(`^(?:${FIELDS_PATTERN})\\s*${SYMBOL_OP_PATTERN}.+$`);
const WORD_JQL_PATTERN = new RegExp(`^(?:${FIELDS_PATTERN})\\s+${WORD_OP_PATTERN}\\b.+$`);
const ORDER_BY_PATTERN = new RegExp(`^order by\\s+(?:${FIELDS_PATTERN}).*$`);
const CHANGED_PATTERN = new RegExp(`^(?:${CHANGED_FIELDS_PATTERN})\\s+changed.*$`);

/**
 * Detects whether a query is JQL, by shape.
 *
 * A *bounded* JQL has a search restriction: a known field, an operator, and at
 * least one more character. An *unbounded* JQL has none — it is either blank,
 * or only an `order by` clause.
 *
 * Note blank input returns `true`, exactly as upstream does: a blank query is
 * treated as unbounded JQL. In practice the empty matcher claims that input
 * first, so this branch is unreachable through `getInputIntentLocal` — it is kept
 * so the port stays faithful when read or reused on its own.
 */
export const isJql = (query: string | null | undefined): boolean => {
	if (query === null || query === undefined) {
		return false;
	}

	if (query.trim().length === 0) {
		return true;
	}

	const lower = query.toLowerCase().trim();

	return (
		ORDER_BY_PATTERN.test(lower) ||
		CHANGED_PATTERN.test(lower) ||
		SYMBOL_JQL_PATTERN.test(lower) ||
		WORD_JQL_PATTERN.test(lower)
	);
};

/**
 * PORTED FROM: the second branch of `GninHeuristicStrategy.classifyInternal`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/services/strategy/GninHeuristicStrategy.java#lines-78
 *
 * ```java
 * if (JqlUtils.isJql(trimmedQuery)) {
 *     return new JiraClassificationResult(GNIN, JQL);
 * }
 * ```
 *
 * All the detection logic lives in the `isJql` heuristic, which is a direct
 * port of `JqlUtils`.
 */
export const jqlMatcher: IntentMatcher = {
	pattern: 'JQL',
	type: INTENT_TYPE.GNIN,
	matches: isJql,
};
